import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  AlertTriangle,
  TrendingUp,
  TrendingDown,
  Users,
  Calendar,
  Download,
  Target,
  XCircle,
  CheckCircle,
  Package,
  MapPin
} from 'lucide-react';
import { useAccessibleLocations } from '../hooks/useAccessibleLocations';
import { reportsService } from '../services/reportsService';
import { DateRangeQuickFilter } from '@/components/ui/date-range-quick-filter';
import { useDateRangeDefaults } from '@/hooks/useDateRangeDefaults';

interface InventoryError {
  id: string;
  date: string;
  cashierId: string;
  cashierName: string;
  productName: string;
  expectedQuantity: number;
  countedQuantity: number;
  difference: number;
  errorType: 'faltante' | 'sobrante';
  errorReason: string;
  monetaryValue: number;
  location: string;
  auditId: string;
}

interface CashierStats {
  cashierName: string;
  totalErrors: number;
  totalCounts: number;
  accuracyRate: number;
  monetaryImpact: number;
  lastErrorDate: string;
}

export const InventoryErrorsReport: React.FC = () => {
  const { quickRange, dateFrom, dateTo, setQuickRange, setManualDates } = useDateRangeDefaults('inventoryErrors');
  const [selectedCashier, setSelectedCashier] = useState('all');
  const [selectedErrorType, setSelectedErrorType] = useState('all');
  const [selectedLocation, setSelectedLocation] = useState('all');
  const [inventoryErrors, setInventoryErrors] = useState<InventoryError[]>([]);
  const [cashierStats, setCashierStats] = useState<CashierStats[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { locationsWithAll, defaultLocationId } = useAccessibleLocations();

  // Auto-seleccionar local cuando se carga
  useEffect(() => {
    if (defaultLocationId) {
      setSelectedLocation(defaultLocationId);
    }
  }, [defaultLocationId]);

  const handleGenerateReport = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await reportsService.getInventoryErrorsReport(selectedLocation, {
        startDate: dateFrom,
        endDate: dateTo,
        cashierId: selectedCashier !== 'all' ? selectedCashier : undefined,
        errorType: selectedErrorType !== 'all' ? selectedErrorType : undefined,
      });
      setInventoryErrors(data.errors || []);
      setCashierStats(data.cashierStats || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cargar el reporte');
      console.error('Error loading inventory errors report:', err);
    } finally {
      setLoading(false);
    }
  };

  const getErrorTypeColor = (type: string) => {
    switch (type) {
      case 'faltante': return 'bg-red-100 text-red-800';
      case 'sobrante': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getErrorTypeIcon = (type: string) => {
    switch (type) {
      case 'faltante': return <TrendingDown className="w-4 h-4" />;
      case 'sobrante': return <TrendingUp className="w-4 h-4" />;
      default: return <AlertTriangle className="w-4 h-4" />;
    }
  };

  const getAccuracyColor = (rate: number) => {
    if (rate >= 95) return 'text-green-600';
    if (rate >= 90) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getAccuracyBadge = (rate: number) => {
    if (rate >= 95) return 'bg-green-100 text-green-800';
    if (rate >= 90) return 'bg-yellow-100 text-yellow-800';
    return 'bg-red-100 text-red-800';
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-PE', {
      style: 'currency',
      currency: 'PEN'
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('es-PE');
  };

  // Calcular KPIs generales
  const totalErrors = inventoryErrors.length;
  const averageAccuracy = cashierStats.length > 0
    ? cashierStats.reduce((sum, cashier) => sum + cashier.accuracyRate, 0) / cashierStats.length
    : 0;
  const criticalCashiers = cashierStats.filter(cashier => cashier.accuracyRate < 90).length;

  // Los datos ya vienen filtrados del API
  const filteredErrors = inventoryErrors;

  return (
    <div className="space-y-6">
      {/* Header y Filtros */}
      <Card>
        <CardContent className="py-3">
          <div className="flex flex-wrap items-end gap-3">
            <div className="space-y-1">
              <Label className="text-xs text-gray-500">Local</Label>
              <Select value={selectedLocation} onValueChange={setSelectedLocation}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {locationsWithAll.map(location => (
                    <SelectItem key={location.id} value={location.id}>
                      <div className="flex items-center">
                        <MapPin className="w-4 h-4 mr-2" />
                        {location.nombre}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1">
              <Label className="text-xs text-gray-500">Rango</Label>
              <DateRangeQuickFilter
                value={quickRange}
                onChange={setQuickRange}
                reportType="inventoryErrors"
              />
            </div>

            <div className="space-y-1">
              <Label className="text-xs text-gray-500">Desde</Label>
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => setManualDates(e.target.value, dateTo)}
                className="w-[140px]"
                disabled={quickRange !== 'custom'}
              />
            </div>

            <div className="space-y-1">
              <Label className="text-xs text-gray-500">Hasta</Label>
              <Input
                type="date"
                value={dateTo}
                onChange={(e) => setManualDates(dateFrom, e.target.value)}
                className="w-[140px]"
                disabled={quickRange !== 'custom'}
              />
            </div>

            <div className="space-y-1">
              <Label className="text-xs text-gray-500">Cajero</Label>
              <Select value={selectedCashier} onValueChange={setSelectedCashier}>
                <SelectTrigger className="w-[160px]">
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1">
              <Label className="text-xs text-gray-500">Tipo error</Label>
              <Select value={selectedErrorType} onValueChange={setSelectedErrorType}>
                <SelectTrigger className="w-[140px]">
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="faltante">Faltante</SelectItem>
                  <SelectItem value="sobrante">Sobrante</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <Button
              onClick={handleGenerateReport}
              disabled={loading}
              className="bg-macartur-primary hover:bg-macartur-primary/90"
            >
              {loading ? 'Cargando...' : 'Generar Reporte'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* KPIs Generales */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Errores</p>
                <p className="text-2xl font-bold text-red-600">{totalErrors}</p>
              </div>
              <XCircle className="w-8 h-8 text-red-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Precisión Promedio</p>
                <p className={`text-2xl font-bold ${getAccuracyColor(averageAccuracy)}`}>
                  {averageAccuracy.toFixed(1)}%
                </p>
              </div>
              <Target className="w-8 h-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Cajeros en Alerta</p>
                <p className="text-2xl font-bold text-orange-600">{criticalCashiers}</p>
              </div>
              <Users className="w-8 h-8 text-orange-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Contenido por pestañas */}
      <Tabs defaultValue="errors" className="space-y-4">
        <TabsList>
          <TabsTrigger value="errors">Errores Detallados</TabsTrigger>
          <TabsTrigger value="cashiers">Rendimiento por Cajero</TabsTrigger>
          <TabsTrigger value="trends">Tendencias</TabsTrigger>
        </TabsList>

        <TabsContent value="errors">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Registro de Errores</CardTitle>
                <Badge variant="outline" className="text-sm">
                  {filteredErrors.length} errores encontrados
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Fecha
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Cajero/Auditor
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Ubicación
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Producto
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                        Esperado
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                        Contado
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                        Diferencia
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                        Tipo
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Motivo
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {filteredErrors.map((error) => (
                      <tr key={error.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900">
                          {formatDate(error.date)}
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-sm font-medium text-gray-900">
                            {error.cashierName}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          {error.location}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          {error.productName}
                        </td>
                        <td className="px-4 py-3 text-center text-sm text-gray-900">
                          {error.expectedQuantity}
                        </td>
                        <td className="px-4 py-3 text-center text-sm text-gray-900">
                          {error.countedQuantity}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className={`text-sm font-medium ${
                            error.difference > 0 ? 'text-blue-600' : 'text-red-600'
                          }`}>
                            {error.difference > 0 ? '+' : ''}{error.difference}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <Badge className={`${getErrorTypeColor(error.errorType)} flex items-center gap-1 w-fit mx-auto`}>
                            {getErrorTypeIcon(error.errorType)}
                            {error.errorType}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          {error.errorReason}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="cashiers">
          <Card>
            <CardHeader>
              <CardTitle>Rendimiento por Cajero</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Cajero/Auditor
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                        Total Errores
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                        Total Conteos
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                        % Precisión
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                        Último Error
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                        Estado
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {cashierStats
                      .sort((a, b) => a.accuracyRate - b.accuracyRate)
                      .map((cashier, index) => (
                      <tr key={cashier.cashierName} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className="text-sm font-medium text-gray-900">
                              {cashier.cashierName}
                            </div>
                            {index === 0 && cashier.accuracyRate < 90 && (
                              <AlertTriangle className="w-4 h-4 text-red-500" />
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-center text-sm text-red-600 font-medium">
                          {cashier.totalErrors}
                        </td>
                        <td className="px-4 py-3 text-center text-sm text-gray-900">
                          {cashier.totalCounts}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <Badge className={`${getAccuracyBadge(cashier.accuracyRate)}`}>
                            {cashier.accuracyRate.toFixed(1)}%
                          </Badge>
                        </td>
                        <td className="px-4 py-3 text-center text-sm text-gray-500">
                          {formatDate(cashier.lastErrorDate)}
                        </td>
                        <td className="px-4 py-3 text-center">
                          {cashier.totalErrors > 10 ? (
                            <Badge className="bg-red-100 text-red-800">
                              Crítico
                            </Badge>
                          ) : cashier.accuracyRate >= 95 ? (
                            <Badge className="bg-green-100 text-green-800">
                              Excelente
                            </Badge>
                          ) : (
                            <Badge className="bg-yellow-100 text-yellow-800">
                              Aceptable
                            </Badge>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="trends">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Productos más Problemáticos</CardTitle>
              </CardHeader>
              <CardContent>
                {(() => {
                  const productCounts: Record<string, number> = {};
                  filteredErrors.forEach(err => {
                    productCounts[err.productName] = (productCounts[err.productName] || 0) + 1;
                  });
                  const sorted = Object.entries(productCounts)
                    .sort(([, a], [, b]) => b - a)
                    .slice(0, 5);
                  if (sorted.length === 0) {
                    return <p className="text-sm text-gray-500 text-center py-4">Sin datos</p>;
                  }
                  return (
                    <div className="space-y-3">
                      {sorted.map(([product, count]) => (
                        <div key={product} className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                          <div>
                            <p className="font-medium text-red-800">{product}</p>
                            <p className="text-sm text-red-600">{count} {count === 1 ? 'error' : 'errores'}</p>
                          </div>
                          <AlertTriangle className="w-5 h-5 text-red-600" />
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Motivos de Error más Comunes</CardTitle>
              </CardHeader>
              <CardContent>
                {(() => {
                  const reasonCounts: Record<string, number> = {};
                  filteredErrors.forEach(err => {
                    reasonCounts[err.errorReason] = (reasonCounts[err.errorReason] || 0) + 1;
                  });
                  const total = filteredErrors.length;
                  const sorted = Object.entries(reasonCounts)
                    .sort(([, a], [, b]) => b - a)
                    .slice(0, 5);
                  if (sorted.length === 0) {
                    return <p className="text-sm text-gray-500 text-center py-4">Sin datos</p>;
                  }
                  return (
                    <div className="space-y-4">
                      {sorted.map(([reason, count]) => {
                        const percentage = total > 0 ? Math.round((count / total) * 100) : 0;
                        return (
                          <div key={reason} className="space-y-2">
                            <div className="flex justify-between text-sm">
                              <span className="font-medium">{reason}</span>
                              <span className="text-gray-600">{count} casos ({percentage}%)</span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2">
                              <div
                                className="bg-macartur-primary h-2 rounded-full"
                                style={{ width: `${percentage}%` }}
                              ></div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};